import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Roulette, RoulettePrize } from 'src/app/models/roulette.model';
import { MessageService } from 'src/app/services/message-service/message.service';
import { RouletteService } from 'src/app/services/roulette-service/roulette.service';
import { UtilService } from 'src/app/services/util/util.service';

const MIN_FULL_TURNS = 5;

const SPIN_DURATION_MS = 5000;

const SPIN_EASING = 'cubic-bezier(0.12, 0.6, 0.08, 1)';

const CENTER = 100;
const SECTOR_RADIUS = 81;
const DOT_RADIUS = 89.5;
const LABEL_RADIUS = 63;
const ICON_RADIUS = 33;
const LABEL_LINE_HEIGHT = 8.5;

const LABEL_SINGLE_LINE_CHARS = 10;

const SECTOR_MARGIN = 0.12;

interface Point {
    x: number;
    y: number;
}

interface RouletteSector {
    prize: RoulettePrize;
    path: string;
    divider: Point;
    label: Point;
    icon: Point;
    labelLines: string[];
}

interface RouletteDot extends Point {
    light: boolean;
}

/**
 * @author Starley Cazorla
 */
@Component({
    selector: 'app-mm-roulette-modal',
    templateUrl: './mm-roulette-modal.component.html',
    styleUrls: ['./mm-roulette-modal.component.scss'],
})
export class MmRouletteModalComponent implements OnInit {

    @Input() gameId: number;
    @Input() canSpin: boolean = true;
    @Input() availableSpins: number = 0;
    @Input() title?: string;
    @Input() message?: string;
    @Input() badgeText?: string | null;
    @Input() prizes: RoulettePrize[] = [];

    @ViewChild('rotor', { static: true }) rotorRef: ElementRef<HTMLElement>;

    public sectors: RouletteSector[] = [];
    public dots: RouletteDot[] = [];

    public readonly starburstPath = this.buildStarburst(12, 7.5, 5.8);
    public readonly labelLineHeight = LABEL_LINE_HEIGHT;

    public spinning: boolean = false;
    public hasSpun: boolean = false;

    public result: RoulettePrize | null = null;

    public emailStep: 'confirm' | 'edit' | null = null;
    public userEmail: string = '';
    public newEmail: string = '';
    public savingEmail: boolean = false;
    public loadingNextSpin: boolean = false;

    private currentAngle: number = 0;

    constructor(
        private modalCtrl: ModalController,
        private rouletteService: RouletteService,
        private messageService: MessageService,
        private utilService: UtilService,
    ) { }

    ngOnInit() {
        this.buildWheel();
    }

    private buildWheel(): void {
        this.sectors = this.buildSectors((this.prizes ?? []).filter(p => p.active));
        this.dots = this.buildDots(this.sectors.length);
    }

    touchClose(): void {
        if (!this.hasSpun) {
            this.rouletteService.reset();
        }

        this.modalCtrl.dismiss({ hasSpun: this.hasSpun, result: this.result });
    }

    touchConfirmEmail(): void {
        this.finishEmailStep('E-mail confirmado com sucesso!');
    }

    touchEditEmail(): void {
        this.newEmail = this.userEmail;
        this.emailStep = 'edit';
    }

    touchCancelEditEmail(): void {
        this.emailStep = 'confirm';
    }

    async touchSaveEmail(): Promise<void> {

        if (this.savingEmail) {
            return;
        }

        const email = this.newEmail.trim();

        if (!this.utilService.validateEmail(email)) {
            this.messageService.showToast('Informe um e-mail válido.', 'warning', 'bottom', 3000, true);
            return;
        }

        if (email === this.userEmail) {
            this.touchConfirmEmail();
            return;
        }

        this.savingEmail = true;
        const updated = await this.rouletteService.updateUserEmail(email);
        this.savingEmail = false;

        if (updated) {
            this.finishEmailStep('E-mail atualizado com sucesso!');
        }
    }

    private async finishEmailStep(message: string): Promise<void> {
        this.messageService.showToast(message, 'success', 'bottom', 3000, true);

        this.result = null;
        this.emailStep = null;
        this.loadingNextSpin = true;

        const nextRoulette = await this.rouletteService.getPlayableRoulette();

        this.loadingNextSpin = false;

        if (!nextRoulette) {
            this.touchClose();
            return;
        }

        this.prepareNextSpin(nextRoulette);
    }

    prepareNextSpin(roulette: Roulette): void {
        this.gameId = roulette.id_jogo;
        this.canSpin = roulette.canSpin;
        this.availableSpins = roulette.availableSpins;
        this.title = roulette.title;
        this.message = roulette.message;
        this.badgeText = roulette.badgeText;
        this.prizes = roulette.prizes;
        this.hasSpun = false;
        this.buildWheel();
    }

    private startEmailStep(): void {
        this.userEmail = this.rouletteService.getUserEmail();
        this.newEmail = this.userEmail;
        this.emailStep = this.userEmail ? 'confirm' : 'edit';
    }

    async touchSpin(): Promise<void> {

        if (this.spinning || this.hasSpun || !this.canSpin || this.sectors.length < 2) {
            return;
        }

        this.spinning = true;

        const spinResult = await this.rouletteService.getRoulettePrizes(this.gameId);

        if (!spinResult) {
            this.spinning = false;
            this.touchClose();
            return;
        }

        const winnerId = spinResult.result.premio?.id_premio;
        const index = this.sectors.findIndex(s => s.prize.id === winnerId);

        if (index < 0) {
            console.error('[roulette] prêmio fora da roda', spinResult);
            this.spinning = false;
            this.touchClose();
            return;
        }

        this.animateTo(index, this.sectors[index].prize);
    }

    private animateTo(index: number, prize: RoulettePrize): void {

        const rotor = this.rotorRef.nativeElement;

        const turnsDone = Math.ceil(this.currentAngle / 360);
        const target = turnsDone * 360 + this.pickAngle(index, this.sectors.length, MIN_FULL_TURNS);

        let finished = false;

        const stopped = () => {
            if (finished) {
                return;
            }
            finished = true;

            this.currentAngle = target;
            this.spinning = false;
            this.hasSpun = true;
            this.result = prize;
            this.startEmailStep();
        };

        rotor.addEventListener('transitionend', stopped, { once: true });

        rotor.style.transition = 'none';
        rotor.style.transform = `rotate(${this.currentAngle}deg)`;

        rotor.getBoundingClientRect();

        rotor.style.transition = `transform ${SPIN_DURATION_MS}ms ${SPIN_EASING}`;
        rotor.style.transform = `rotate(${target}deg)`;

        setTimeout(stopped, SPIN_DURATION_MS + 400);
    }

    private buildSectors(prizes: RoulettePrize[]): RouletteSector[] {
        const step = 360 / prizes.length;

        return prizes.map((prize, i) => ({
            prize,
            path: this.sectorPath((i - 0.5) * step, (i + 0.5) * step),
            divider: this.polar((i - 0.5) * step, SECTOR_RADIUS),
            label: this.polar(i * step, LABEL_RADIUS),
            icon: this.polar(i * step, ICON_RADIUS),
            labelLines: this.splitLabel(prize.label)
        }));
    }

    private buildDots(count: number): RouletteDot[] {
        const step = 360 / count;
        const dots: RouletteDot[] = [];

        for (let i = 0; i < count; i++) {
            dots.push({ ...this.polar(i * step, DOT_RADIUS), light: true });
            dots.push({ ...this.polar((i + 0.5) * step, DOT_RADIUS), light: false });
        }

        return dots;
    }

    private sectorPath(startDeg: number, endDeg: number): string {
        const p0 = this.polar(startDeg, SECTOR_RADIUS);
        const p1 = this.polar(endDeg, SECTOR_RADIUS);
        const largeArc = endDeg - startDeg > 180 ? 1 : 0;

        return `M ${CENTER} ${CENTER} L ${p0.x} ${p0.y} A ${SECTOR_RADIUS} ${SECTOR_RADIUS} 0 ${largeArc} 1 ${p1.x} ${p1.y} Z`;
    }

    private splitLabel(label: string): string[] {
        const words = (label ?? '').split(/\s+/).filter(Boolean);
        const full = words.join(' ');

        if (words.length < 2 || full.length <= LABEL_SINGLE_LINE_CHARS) {
            return full ? [full] : [];
        }

        let best: string[] = [full];
        let bestWidth = Infinity;

        for (let cut = 1; cut < words.length; cut++) {
            const lines = [words.slice(0, cut).join(' '), words.slice(cut).join(' ')];
            const width = Math.max(lines[0].length, lines[1].length);

            if (width < bestWidth) {
                best = lines;
                bestWidth = width;
            }
        }

        return best;
    }

    private polar(deg: number, radius: number): Point {
        const rad = deg * Math.PI / 180;
        return {
            x: +(CENTER + radius * Math.sin(rad)).toFixed(3),
            y: +(CENTER - radius * Math.cos(rad)).toFixed(3)
        };
    }

    private buildStarburst(points: number, outer: number, inner: number): string {
        const step = 180 / points;
        const coords: string[] = [];

        for (let i = 0; i < points * 2; i++) {
            const r = i % 2 === 0 ? outer : inner;
            const rad = i * step * Math.PI / 180;
            coords.push(`${(r * Math.sin(rad)).toFixed(2)} ${(-r * Math.cos(rad)).toFixed(2)}`);
        }

        return `M ${coords.join(' L ')} Z`;
    }

    private randomInt(max: number): number {
        if (max <= 0) {
            throw new Error('randomInt: max deve ser > 0');
        }

        const cryptoObj: Crypto = (window.crypto || (window as any).msCrypto);
        const limit = Math.floor(0xFFFFFFFF / max) * max;
        const buffer = new Uint32Array(1);

        let value: number;
        do {
            cryptoObj.getRandomValues(buffer);
            value = buffer[0];
        } while (value >= limit);

        return value % max;
    }

    private pickAngle(index: number, count: number, minFullTurns: number = 5): number {
        const sectorAngle = 360 / count;
        const margin = sectorAngle * SECTOR_MARGIN;
        const start = (index - 0.5) * sectorAngle + margin;
        const range = sectorAngle - margin * 2;

        const target = start + this.randomInt(Math.max(1, Math.round(range * 10))) / 10;

        return minFullTurns * 360 + (360 - target);
    }
}
